import { useMemo, useRef, useState } from 'react';

/**
 * Autotask Data Map › Database diagram — every entity as a table, grouped by
 * area in coloured regions, with a connector from each field that holds an id
 * to the table it points to (the field's `reference` in autotaskEntities.json).
 *
 * Tables show their key (id) and their linking fields by default; "All fields"
 * shows every column. Selecting a table highlights the connectors in and out
 * of it and dims the rest. Laid out here, not by hand: areas flow into three
 * columns and tables into two columns per area, shortest column first.
 */

export interface ErdField {
  name: string;
  type: string;
  reference: string | null;
}
export interface ErdEntity {
  name: string;
  description: string;
  fields: ErdField[];
}
export interface ErdArea {
  name: string;
  entities: string[];
}

const BOX_W = 268;
/* Characters that fit beside the icon and the type column at 11px. */
const LABEL_CHARS = 36;
const HEAD_H = 26;
const ROW_H = 17;
const PAD_B = 6;
const GAP = 26; /* between tables in a region */
const REGION_PAD = 18;
const REGION_TOP = 34; /* room for the region's name */
const REGION_GAP = 56; /* between regions — room for connectors */
const AREA_COLS = 3;
const BOX_COLS = 2;

/* One soft colour per area, from the chart series tokens. */
const TINTS = ['--series-1', '--series-2', '--series-3', '--series-4', '--series-5', '--series-6'];

interface Box {
  name: string;
  area: number;
  x: number;
  y: number;
  h: number;
  rows: { name: string; type: string; ref: string | null; key: boolean }[];
}
interface Region {
  name: string;
  x: number;
  y: number;
  w: number;
  h: number;
  tint: string;
}

function layout(entities: Map<string, ErdEntity>, areas: ErdArea[], allFields: boolean) {
  const boxes = new Map<string, Box>();
  const regions: Region[] = [];
  const regionW = BOX_COLS * BOX_W + (BOX_COLS - 1) * GAP + REGION_PAD * 2;
  const colY = new Array(AREA_COLS).fill(0) as number[];

  areas.forEach((area, ai) => {
    const list = area.entities.filter((n) => entities.has(n));
    if (!list.length) return;
    /* The area's tables, two columns, shortest first. */
    const inner = new Array(BOX_COLS).fill(0) as number[];
    const placed: { name: string; col: number; y: number; h: number; rows: Box['rows'] }[] = [];
    for (const name of list) {
      const e = entities.get(name)!;
      const keys = e.fields.filter((f) => f.reference);
      const rest = allFields ? e.fields.filter((f) => !f.reference && f.name !== 'id') : [];
      const rows: Box['rows'] = [
        { name: 'id', type: 'integer', ref: null, key: true },
        ...keys.map((f) => ({ name: f.name, type: f.type, ref: f.reference, key: false })),
        ...rest.map((f) => ({ name: f.name, type: f.type, ref: null, key: false })),
      ];
      const h = HEAD_H + rows.length * ROW_H + PAD_B;
      const col = inner.indexOf(Math.min(...inner));
      placed.push({ name, col, y: inner[col]!, h, rows });
      inner[col] = inner[col]! + h + GAP;
    }
    const regionH = REGION_TOP + Math.max(...inner) - GAP + REGION_PAD;
    const ac = colY.indexOf(Math.min(...colY));
    const rx = ac * (regionW + REGION_GAP);
    const ry = colY[ac]!;
    colY[ac] = ry + regionH + REGION_GAP;
    regions.push({ name: area.name, x: rx, y: ry, w: regionW, h: regionH, tint: TINTS[ai % TINTS.length]! });
    for (const p of placed) {
      boxes.set(p.name, {
        name: p.name,
        area: ai,
        x: rx + REGION_PAD + p.col * (BOX_W + GAP),
        y: ry + REGION_TOP + p.y,
        h: p.h,
        rows: p.rows,
      });
    }
  });
  const width = AREA_COLS * regionW + (AREA_COLS - 1) * REGION_GAP;
  const height = Math.max(...colY) - REGION_GAP;
  return { boxes, regions, width, height };
}

interface Connector {
  key: string;
  from: string;
  field: string;
  to: string;
  d: string;
}

/** Elbow connectors: out of the field's row on the side facing the target, into the target's id row. */
function connectors(boxes: Map<string, Box>): Connector[] {
  const out: Connector[] = [];
  let lane = 0;
  for (const b of boxes.values()) {
    b.rows.forEach((r, i) => {
      if (!r.ref) return;
      const t = boxes.get(r.ref);
      if (!t) return;
      const sy = b.y + HEAD_H + i * ROW_H + ROW_H / 2;
      const ty = t.y + HEAD_H + ROW_H / 2;
      lane = (lane + 1) % 6;
      const off = 10 + lane * 4;
      let d: string;
      if (t.name === b.name) {
        const x = b.x + BOX_W;
        d = `M${x},${sy} H${x + off + 8} V${ty} H${x}`;
      } else if (t.x > b.x + BOX_W) {
        const sx = b.x + BOX_W;
        const tx = t.x;
        const mx = Math.min(sx + (tx - sx) / 2, sx + REGION_GAP) + (lane - 3) * 3;
        d = `M${sx},${sy} H${mx} V${ty} H${tx}`;
      } else if (t.x + BOX_W < b.x) {
        const sx = b.x;
        const tx = t.x + BOX_W;
        const mx = Math.max(sx - (sx - tx) / 2, sx - REGION_GAP) + (lane - 3) * 3;
        d = `M${sx},${sy} H${mx} V${ty} H${tx}`;
      } else {
        /* The same column: out to the right and back. */
        const sx = b.x + BOX_W;
        const tx = t.x + BOX_W;
        const mx = Math.max(sx, tx) + off;
        d = `M${sx},${sy} H${mx} V${ty} H${tx}`;
      }
      out.push({ key: `${b.name}.${r.name}`, from: b.name, field: r.name, to: t.name, d });
    });
  }
  return out;
}

export function AutotaskErd({
  entities,
  areas,
  selected,
  onSelect,
  used,
}: {
  entities: ErdEntity[];
  areas: ErdArea[];
  selected: string;
  onSelect: (name: string) => void;
  used: Record<string, string>;
}) {
  const [allFields, setAllFields] = useState(false);
  const [zoom, setZoom] = useState(0.8);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [focusOnly, setFocusOnly] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);

  const byName = useMemo(() => new Map(entities.map((e) => [e.name, e])), [entities]);
  const shownAreas = useMemo(() => areas.filter((a) => !hidden.has(a.name)), [areas, hidden]);
  const { boxes, regions, width, height } = useMemo(() => layout(byName, shownAreas, allFields), [byName, shownAreas, allFields]);
  const lines = useMemo(() => connectors(boxes), [boxes]);

  const related = useMemo(() => {
    const s = new Set<string>([selected]);
    for (const l of lines) if (l.from === selected || l.to === selected) s.add(l.from === selected ? l.to : l.from);
    return s;
  }, [lines, selected]);
  const hasSel = boxes.has(selected);
  const isOn = (l: Connector) => l.from === selected || l.to === selected;

  const fit = () => {
    const w = scroller.current?.clientWidth ?? 1200;
    setZoom(Math.max(0.25, Math.min(1.5, Math.floor(((w - 24) / width) * 100) / 100)));
  };
  const toggleArea = (name: string) =>
    setHidden((s) => {
      const n = new Set(s);
      if (n.has(name)) n.delete(name);
      else n.add(name);
      return n;
    });

  return (
    <div className="erd">
      <div className="row wrap erd-tools" style={{ gap: 'var(--space-2)' }}>
        <div className="row" style={{ gap: 4 }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setZoom((z) => Math.max(0.25, Math.round((z - 0.1) * 100) / 100))} aria-label="Zoom out">
            −
          </button>
          <span className="mono xs" style={{ width: 44, textAlign: 'center' }}>
            {Math.round(zoom * 100)}%
          </span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setZoom((z) => Math.min(2, Math.round((z + 0.1) * 100) / 100))} aria-label="Zoom in">
            +
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={fit}>
            Fit
          </button>
        </div>
        <label className="checkbox small">
          <input type="checkbox" checked={allFields} onChange={(e) => setAllFields(e.target.checked)} />
          All fields (not just keys and links)
        </label>
        <label className="checkbox small">
          <input type="checkbox" checked={focusOnly} onChange={(e) => setFocusOnly(e.target.checked)} />
          Only the selected table&apos;s connectors
        </label>
        <span className="xs muted">
          {num(boxes.size)} tables · {num(lines.length)} connectors
        </span>
      </div>
      <div className="row wrap erd-areas" style={{ gap: 4 }}>
        {areas.map((a, i) => (
          <button
            key={a.name}
            type="button"
            className={`erd-area-chip${hidden.has(a.name) ? ' is-off' : ''}`}
            style={{ ['--tint' as string]: `var(${TINTS[i % TINTS.length]})` }}
            onClick={() => toggleArea(a.name)}
            title={hidden.has(a.name) ? 'Show this area' : 'Hide this area'}
          >
            {a.name}
          </button>
        ))}
      </div>

      <div className="erd-scroll" ref={scroller}>
        <svg
          width={width * zoom + 24}
          height={height * zoom + 24}
          viewBox={`-12 -12 ${width + 24} ${height + 24}`}
          className="erd-svg"
          role="img"
          aria-label="Autotask entity relationship diagram"
        >
          <defs>
            {/* "Many" at the field end, "one" at the table it points to. */}
            <marker id="erd-many" viewBox="0 0 12 12" refX="1" refY="6" markerWidth="10" markerHeight="10" orient="auto-start-reverse">
              <path d="M12,6 L1,1 M12,6 L1,11 M12,6 L1,6" className="erd-mark" />
            </marker>
            <marker id="erd-one" viewBox="0 0 12 12" refX="11" refY="6" markerWidth="10" markerHeight="10" orient="auto">
              <path d="M9,1 L9,11 M5,1 L5,11" className="erd-mark" />
            </marker>
          </defs>

          {regions.map((r) => (
            <g key={r.name}>
              <rect x={r.x} y={r.y} width={r.w} height={r.h} rx={6} className="erd-region" style={{ ['--tint' as string]: `var(${r.tint})` }} />
              <text x={r.x + 10} y={r.y + 20} className="erd-region-name">
                {r.name}
              </text>
            </g>
          ))}

          <g className="erd-lines">
            {lines
              .filter((l) => !focusOnly || isOn(l))
              .map((l) => (
                <path
                  key={l.key}
                  d={l.d}
                  className={`erd-line${hasSel && isOn(l) ? ' is-on' : ''}${hasSel && !isOn(l) ? ' is-dim' : ''}`}
                  markerStart="url(#erd-many)"
                  markerEnd="url(#erd-one)"
                >
                  <title>{`${l.from}.${l.field} → ${l.to}.id`}</title>
                </path>
              ))}
          </g>

          {[...boxes.values()].map((b) => {
            const dim = hasSel && !related.has(b.name);
            return (
              <g
                key={b.name}
                className={`erd-box${b.name === selected ? ' is-on' : ''}${dim ? ' is-dim' : ''}`}
                onClick={() => onSelect(b.name)}
                role="button"
                aria-label={`${b.name} table`}
              >
                <title>{byName.get(b.name)?.description ?? b.name}</title>
                <rect x={b.x} y={b.y} width={BOX_W} height={b.h} rx={3} className="erd-box-bg" />
                <rect x={b.x} y={b.y} width={BOX_W} height={HEAD_H} rx={3} className="erd-box-head" />
                <text x={b.x + 8} y={b.y + 17} className="erd-box-name">
                  {b.name}
                </text>
                {used[b.name] && (
                  <circle cx={b.x + BOX_W - 11} cy={b.y + HEAD_H / 2} r={4} className="erd-used">
                    <title>{`MSP-OPS reads it: ${used[b.name]}`}</title>
                  </circle>
                )}
                {b.rows.map((r, i) => {
                  const y = b.y + HEAD_H + i * ROW_H;
                  const target = r.ref;
                  return (
                    <g key={r.name} className={target ? 'erd-row is-fk' : r.key ? 'erd-row is-pk' : 'erd-row'}>
                      <text x={b.x + 8} y={y + 12.5} className="erd-row-icon">
                        {r.key ? '🔑' : target ? '◆' : '◦'}
                      </text>
                      <text x={b.x + 22} y={y + 12.5} className="erd-row-name">
                        <title>{`${r.name}${target ? ` → ${target}.id` : ''} · ${r.type}`}</title>
                        {clip(`${r.name}${target ? ` → ${target}` : ''}`)}
                      </text>
                      <text x={b.x + BOX_W - 6} y={y + 12.5} textAnchor="end" className="erd-row-type">
                        {shortType(r.type)}
                      </text>
                    </g>
                  );
                })}
              </g>
            );
          })}
        </svg>
      </div>
      <p className="xs muted mt-2">
        🔑 the table&apos;s id (primary key) · ◆ a field holding another table&apos;s id (foreign key), with the connector from it to that
        table — crow&apos;s foot at the field (many), double bar at the table (one) · <span className="erd-used-key" /> MSP-OPS reads it today.
        Click a table to highlight its connectors and open its fields below. Links to tables not in this map are named on the row but
        have no connector.
      </p>
    </div>
  );
}

const num = (n: number) => n.toLocaleString();
const clip = (label: string) => (label.length > LABEL_CHARS ? `${label.slice(0, LABEL_CHARS - 1)}…` : label);
function shortType(t: string) {
  const s = t.replace(/^string\s*\((\d+)\)$/i, 'str($1)').replace(/^datetime$/i, 'datetime').replace(/^integer$/i, 'int').replace(/^boolean$/i, 'bool');
  return s.length > 12 ? `${s.slice(0, 11)}…` : s;
}
